import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import apiService from '../services/api';

const DepartmentList = () => {
  const [departments, setDepartments] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingDepartment, setEditingDepartment] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    company_id: '',
    created_by: 1
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [departmentsResponse, companiesResponse] = await Promise.all([
        apiService.departments.getAll(),
        apiService.companies.getAll()
      ]);
      setDepartments(departmentsResponse.data);
      setCompanies(companiesResponse.data);
    } catch (err) {
      setError('Failed to load data');
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const dataToSubmit = {
        ...formData,
        company_id: parseInt(formData.company_id)
      };

      if (editingDepartment) {
        await apiService.departments.update(editingDepartment.id, {
          ...dataToSubmit,
          updated_by: 1
        });
      } else {
        await apiService.departments.create(dataToSubmit);
      }
      
      setShowForm(false);
      setEditingDepartment(null);
      setFormData({ name: '', company_id: '', created_by: 1 });
      fetchData();
    } catch (err) {
      setError('Failed to save department');
      console.error('Error saving department:', err);
    }
  };

  const handleEdit = (department) => {
    setEditingDepartment(department);
    setFormData({
      name: department.name,
      company_id: department.company_id.toString(),
      created_by: department.created_by || 1
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this department?')) {
      try {
        await apiService.departments.delete(id);
        fetchData();
      } catch (err) {
        setError('Failed to delete department');
        console.error('Error deleting department:', err);
      }
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingDepartment(null);
    setFormData({ name: '', company_id: '', created_by: 1 });
  };

  const getCompanyName = (companyId) => {
    const company = companies.find(c => c.id === companyId);
    return company ? company.name : 'Unknown';
  };

  if (loading) {
    return <div className="loading">Loading departments...</div>;
  }

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Departments</h1>
        <button 
          className="btn btn-primary" 
          onClick={() => setShowForm(true)}
        >
          Add Department
        </button>
      </div>

      {error && <div className="error">{error}</div>}

      {showForm && (
        <div className="form">
          <h3>{editingDepartment ? 'Edit Department' : 'Add New Department'}</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">Department Name *</label>
              <input
                type="text"
                className="form-control"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">Company *</label>
              <select
                className="form-control"
                value={formData.company_id}
                onChange={(e) => setFormData({...formData, company_id: e.target.value})}
                required
              >
                <option value="">Select a company</option>
                {companies.map(company => (
                  <option key={company.id} value={company.id}>
                    {company.name}
                  </option>
                ))}
              </select>
            </div>
            <div style={{display: 'flex', gap: '1rem'}}>
              <button type="submit" className="btn btn-primary">
                {editingDepartment ? 'Update' : 'Create'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={resetForm}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="table-container">
        <table className="table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Name</th>
              <th>Company</th>
              <th>Created At</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {departments.map(department => (
              <tr key={department.id}>
                <td>{department.id}</td>
                <td>
                  <Link 
                    to={`/departments/${department.id}`}
                    style={{color: '#034955', textDecoration: 'none', fontWeight: '500'}}
                    onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                    onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                  >
                    {department.name}
                  </Link>
                </td>
                <td>{getCompanyName(department.company_id)}</td>
                <td>{new Date(department.created_at).toLocaleDateString()}</td>
                <td>
                  <Link 
                    to={`/departments/${department.id}`}
                    className="btn btn-primary btn-small"
                    style={{marginRight: '0.5rem'}}
                  >
                    Details
                  </Link>
                  <button 
                    className="btn btn-secondary btn-small" 
                    onClick={() => handleEdit(department)}
                    style={{marginRight: '0.5rem'}}
                  >
                    Edit
                  </button>
                  <button 
                    className="btn btn-danger btn-small" 
                    onClick={() => handleDelete(department.id)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {departments.length === 0 && (
          <div style={{padding: '2rem', textAlign: 'center', color: '#6c757d'}}>
            No departments found. Add one to get started!
          </div>
        )}
      </div>
    </div>
  );
};

export default DepartmentList;